import React, { useContext, useState } from 'react';
import { useLocalizedStrings } from '../../Localization';
import { PublicConfigurationContext } from '../../Context';
import { useHistory } from 'react-router-dom';
import { Badge, Button, CompactSidebarNavTrigger, DataTable, Heading, Icon, NumberDisplay, Page, SidebarNavTrigger, Tooltip } from '@aidkitorg/component-library';
import { AdminPanelNav } from '../AdminPanelApp';
import { ColumnDef } from '@tanstack/react-table';
import { TableAction } from '@aidkitorg/component-library/dist/types/src/DataTable';
import { Filters, PaymentsFilter } from './PaymentsFilter';
import { Payment } from '@aidkitorg/roboscreener/lib/compute/derived/Payment';

// FAKE PAYMENTS DATA
// TODO: move to API or PaymentsService
export type Payment = {
  batchName: string
  paymentEnabledStatus: 'enabled' | 'disabled',
  recipients: number
  failed: number
  totalAmount: number
  status: 'approved' | 'pending_approval'
  notifications: 'enabled' | 'disabled'
  firstPaymentDate: Date
  lastPaymentDate: Date
}

const paymentColumns: ColumnDef<Payment>[] = [
  {
    accessorKey: "batchName",
    header: "Batch Name",
  },
  {
    accessorKey: "paymentEnabledStatus",
    header: "Payment Enabled",
    cell: ({ row }) => {
      const paymentEnabledStatus = row.original.paymentEnabledStatus
      const label =  paymentEnabledStatus === 'enabled' ? "Enabled" : "Disabled"
      const variant = paymentEnabledStatus === 'enabled' ? 'success' : 'neutral'
      return <Badge variant={variant}>{label}</Badge>
    },
  },
  {
    accessorKey: "recipients",
    header: "Recipients",
    cell: ({ row }) => {
      return (<Button variant="link" href="/admin-panel/payments/payment-details/payment-id">{row.original.recipients} recipients</Button>)
    }
  },
  {
    accessorKey: "failed",
    header: "Failed",
    filterFn: (row, columnId, filterValue) => {
      return filterValue && row.original.failed > 0;
    }
  },
  {
    accessorKey: "totalAmount",
    header: "Total Amount",
    cell: ({ row }) => (
      <span>${new Intl.NumberFormat().format(row.original.totalAmount)}</span>
    ),
    filterFn: (row, columnId, filterValue) => {
      const amount = row.getValue(columnId) as number
      const min = filterValue[0]
      const max = filterValue[1]

      return amount >= min && amount <= max
    }
  },
  {
    accessorKey: "status",
    header: "Status",
    cell: ({ row }) => {
      const status = row.original.status
      const variant = status === 'approved' ? 'success' : 'warning'
      const copy = status === 'approved' ? "Approved" : "Pending"
      return (
        <Badge variant={variant} >{copy}</Badge>
      )
    },
  },
  {
    accessorKey: "notifications",
    header: "Notifications",
    cell: ({ row }) => {
      const status = row.original.notifications
      const label = status === 'enabled' ? "Enabled" : "Disabled"
      const variant = status === 'enabled' ? 'success' : 'neutral'
      return <Badge variant={variant}>{label}</Badge>
    },
  },
  {
    accessorKey: "firstPaymentDate",
    header: "First Payment",
    cell: ({ row }) => {
      const date = row.original.firstPaymentDate
      const formatted = new Intl.DateTimeFormat("en-US", {
        month: "short",
        day: "numeric",
        year: "numeric",
      }).format(date)

      return <span>{formatted}</span>
    },
    sortingFn: (rowA, rowB) => {
      const a = rowA.original.firstPaymentDate.getTime()
      const b = rowB.original.firstPaymentDate.getTime()
      return a - b
    },
    filterFn: (row, columnId, filterValue) => {
      if (!filterValue?.firstPaymentStart || !filterValue?.firstPaymentEnd) return true
      const date = row.getValue(columnId) as Date
      return (
        date >= new Date(filterValue.firstPaymentStart) &&
        date <= new Date(filterValue.firstPaymentEnd)
      )
    },
  },
  {
    accessorKey: "lastPaymentDate",
    header: "Last Payment",
    cell: ({ row }) => {
      const date = row.original.lastPaymentDate
      const formatted = new Intl.DateTimeFormat("en-US", {
        month: "short",
        day: "numeric",
        year: "numeric",
      }).format(date)

      return <span>{formatted}</span>
    },
    sortingFn: (rowA, rowB) => {
      const a = rowA.original.lastPaymentDate.getTime()
      const b = rowB.original.lastPaymentDate.getTime()
      return a - b
    },
    filterFn: (row, columnId, filterValue) => {
      if (!filterValue?.lastPaymentStart || !filterValue?.lastPaymentEnd) return true
      const date = row.getValue(columnId) as Date
      return (
        date >= new Date(filterValue.lastPaymentStart) &&
        date <= new Date(filterValue.lastPaymentEnd)
      )
    },
  },
]

const paymentData: Payment[] = [
  {
    batchName: "March Housing Assistance",
    recipients: 25,
    failed: 2,
    totalAmount: 125000,
    status: "pending_approval",
    firstPaymentDate: new Date("2024-03-25"),
    lastPaymentDate: new Date("2024-03-31"),
    notifications: "disabled",
    paymentEnabledStatus: 'disabled',
  },
  {
    batchName: "Emergency Rental Support",
    recipients: 30,
    failed: 0,
    totalAmount: 75000,
    status: "approved",
    firstPaymentDate: new Date("2024-04-10"),
    lastPaymentDate: new Date("2024-04-14"),
    notifications: "enabled",
    paymentEnabledStatus: 'enabled',
  },
  {
    batchName: "LA County Wildfire Support",
    recipients: 198,
    failed: 0,
    totalAmount: 890000,
    status: "approved",
    firstPaymentDate: new Date("2025-01-05"),
    lastPaymentDate: new Date("2025-01-10"),
    notifications: "enabled",
    paymentEnabledStatus: 'enabled',
  },
  {
    batchName: "RXKids Payment 1",
    recipients: 48,
    failed: 0,
    totalAmount: 39000,
    status: "pending_approval",
    firstPaymentDate: new Date("2024-10-10"),
    lastPaymentDate: new Date("2024-10-14"),
    notifications: "disabled",
    paymentEnabledStatus: 'disabled',
  },
  {
    batchName: "Senior Care Batch 5",
    recipients: 71,
    failed: 2,
    totalAmount: 28755,
    status: "approved",
    firstPaymentDate: new Date("2024-01-03"),
    lastPaymentDate: new Date("2024-01-04"),
    notifications: "enabled",
    paymentEnabledStatus: 'enabled',
  },
  {
    batchName: "Food Relief Batch 6",
    recipients: 29,
    failed: 0,
    totalAmount: 87058,
    status: "pending_approval",
    firstPaymentDate: new Date("2023-06-17"),
    lastPaymentDate: new Date("2023-06-20"),
    notifications: "enabled",
    paymentEnabledStatus: 'enabled',
  },
  {
    batchName: "Food Relief Batch 7",
    recipients: 39,
    failed: 1,
    totalAmount: 26286,
    status: "pending_approval",
    firstPaymentDate: new Date("2024-11-29"),
    lastPaymentDate: new Date("2024-12-04"),
    notifications: "enabled",
    
    paymentEnabledStatus: 'disabled',
  },
  {
    batchName: "Winter Emergency Fund Batch 8",
    recipients: 57,
    failed: 2,
    totalAmount: 207081,
    status: "approved",
    firstPaymentDate: new Date("2024-05-09"),
    lastPaymentDate: new Date("2024-05-11"),
    notifications: "disabled",
    paymentEnabledStatus: 'enabled',
  },
  {
    batchName: "Utility Stipend Batch 9",
    recipients: 46,
    failed: 0,
    totalAmount: 119508,
    status: "pending_approval",
    firstPaymentDate: new Date("2023-03-03"),
    lastPaymentDate: new Date("2023-03-06"),
    notifications: "disabled",
    paymentEnabledStatus: 'enabled',
  },
  {
    batchName: "Back-to-School Aid Batch 10",
    recipients: 64,
    failed: 5,
    totalAmount: 195200,
    status: "pending_approval",
    firstPaymentDate: new Date("2024-07-23"),
    lastPaymentDate: new Date("2024-07-26"),
    notifications: "disabled",
    paymentEnabledStatus: 'enabled',
  },
  {
    batchName: "Back-to-School Aid Batch 11",
    recipients: 31,
    failed: 1,
    totalAmount: 79081,
    status: "approved",
    firstPaymentDate: new Date("2024-03-02"),
    lastPaymentDate: new Date("2024-03-06"),
    notifications: "disabled",
    paymentEnabledStatus: 'enabled',
  },
  {
    batchName: "Winter Emergency Fund Batch 12",
    recipients: 77,
    failed: 0,
    totalAmount: 305305,
    status: "approved",
    firstPaymentDate: new Date("2023-04-20"),
    lastPaymentDate: new Date("2023-04-24"),
    notifications: "disabled",
    paymentEnabledStatus: 'enabled',
  },
  {
    batchName: "Back-to-School Aid Batch 13",
    recipients: 21,
    failed: 4,
    totalAmount: 3780,
    status: "pending_approval",
    firstPaymentDate: new Date("2023-08-10"),
    lastPaymentDate: new Date("2023-08-14"),
    notifications: "enabled",
    paymentEnabledStatus: 'enabled',
  },
  {
    batchName: "Back-to-School Aid Batch 14",
    recipients: 95,
    failed: 0,
    totalAmount: 343710,
    status: "approved",
    firstPaymentDate: new Date("2023-07-09"),
    lastPaymentDate: new Date("2023-07-14"),
    notifications: "disabled",
    paymentEnabledStatus: 'enabled',
  },
  {
    batchName: "Winter Emergency Fund Batch 15",
    recipients: 31,
    failed: 5,
    totalAmount: 112468,
    status: "approved",
    firstPaymentDate: new Date("2023-02-11"),
    lastPaymentDate: new Date("2023-02-15"),
    notifications: "enabled",
    paymentEnabledStatus: 'enabled',
  },
  {
    batchName: "Food Relief Batch 16",
    recipients: 41,
    failed: 5,
    totalAmount: 106887,
    status: "approved",
    firstPaymentDate: new Date("2024-01-06"),
    lastPaymentDate: new Date("2024-01-08"),
    notifications: "enabled",
    paymentEnabledStatus: 'enabled',
  },
  {
    batchName: "Food Relief Batch 17",
    recipients: 33,
    failed: 3,
    totalAmount: 80223,
    status: "approved",
    firstPaymentDate: new Date("2023-05-10"),
    lastPaymentDate: new Date("2023-05-14"),
    notifications: "disabled",
    paymentEnabledStatus: 'enabled',
  },
  {
    batchName: "Back-to-School Aid Batch 18",
    recipients: 37,
    failed: 2,
    totalAmount: 15503,
    status: "approved",
    firstPaymentDate: new Date("2023-11-23"),
    lastPaymentDate: new Date("2023-11-24"),
    notifications: "disabled",
    paymentEnabledStatus: 'enabled',
  },
  {
    batchName: "Winter Emergency Fund Batch 19",
    recipients: 50,
    failed: 0,
    totalAmount: 212400,
    status: "pending_approval",
    firstPaymentDate: new Date("2024-12-15"),
    lastPaymentDate: new Date("2024-12-20"),
    notifications: "enabled",
    paymentEnabledStatus: 'enabled',
  },
  {
    batchName: "Food Relief Batch 20",
    recipients: 106,
    failed: 5,
    totalAmount: 250690,
    status: "pending_approval",
    firstPaymentDate: new Date("2023-01-10"),
    lastPaymentDate: new Date("2023-01-15"),
    notifications: "disabled",
    paymentEnabledStatus: 'enabled',
  },
  {
    batchName: "Senior Care Batch 21",
    recipients: 86,
    failed: 5,
    totalAmount: 255248,
    status: "pending_approval",
    firstPaymentDate: new Date("2024-05-28"),
    lastPaymentDate: new Date("2024-05-31"),
    notifications: "enabled",
    paymentEnabledStatus: 'disabled'
  },
  {
    batchName: "Back-to-School Aid Batch 22",
    recipients: 55,
    failed: 4,
    totalAmount: 80190,
    status: "pending_approval",
    firstPaymentDate: new Date("2023-08-24"),
    lastPaymentDate: new Date("2023-08-25"),
    notifications: "disabled",
    paymentEnabledStatus: 'enabled',
  },
  {
    batchName: "Senior Care Batch 23",
    recipients: 61,
    failed: 4,
    totalAmount: 161528,
    status: "pending_approval",
    firstPaymentDate: new Date("2024-07-25"),
    lastPaymentDate: new Date("2024-07-26"),
    notifications: "disabled",
    paymentEnabledStatus: 'enabled',
  },
  {
    batchName: "Utility Stipend Batch 24",
    recipients: 71,
    failed: 0,
    totalAmount: 63190,
    status: "approved",
    firstPaymentDate: new Date("2024-01-19"),
    lastPaymentDate: new Date("2024-01-20"),
    notifications: "disabled",
    paymentEnabledStatus: 'enabled',
  },
  {
    batchName: "Back-to-School Aid Batch 25",
    recipients: 21,
    failed: 3,
    totalAmount: 18963,
    status: "pending_approval",
    firstPaymentDate: new Date("2024-10-02"),
    lastPaymentDate: new Date("2024-10-05"),
    notifications: "enabled",
    paymentEnabledStatus: 'enabled',
  },
  {
    batchName: "Utility Stipend Batch 26",
    recipients: 20,
    failed: 5,
    totalAmount: 94980,
    status: "pending_approval",
    firstPaymentDate: new Date("2023-11-05"),
    lastPaymentDate: new Date("2023-11-08"),
    notifications: "disabled",
    paymentEnabledStatus: 'enabled',
  },
  {
    batchName: "Food Relief Batch 27",
    recipients: 21,
    failed: 5,
    totalAmount: 12894,
    status: "pending_approval",
    firstPaymentDate: new Date("2023-06-30"),
    lastPaymentDate: new Date("2023-07-04"),
    notifications: "disabled",
    paymentEnabledStatus: 'enabled',
  },
  {
    batchName: "Winter Emergency Fund Batch 28",
    recipients: 23,
    failed: 3,
    totalAmount: 96209,
    status: "approved",
    firstPaymentDate: new Date("2024-03-21"),
    lastPaymentDate: new Date("2024-03-26"),
    notifications: "disabled",
    paymentEnabledStatus: 'enabled',
  },
  {
    batchName: "Senior Care Batch 29",
    recipients: 44,
    failed: 2,
    totalAmount: 166144,
    status: "approved",
    firstPaymentDate: new Date("2023-02-21"),
    lastPaymentDate: new Date("2023-02-26"),
    notifications: "enabled",
    paymentEnabledStatus: 'disabled',
  },
  {
    batchName: "Senior Care Batch 30",
    recipients: 50,
    failed: 5,
    totalAmount: 50100,
    status: "approved",
    firstPaymentDate: new Date("2024-10-19"),
    lastPaymentDate: new Date("2024-10-22"),
    notifications: "enabled",
    paymentEnabledStatus: 'disabled',
  },
  {
    batchName: "Winter Emergency Fund Batch 31",
    recipients: 54,
    failed: 2,
    totalAmount: 262332,
    status: "pending_approval",
    firstPaymentDate: new Date("2023-07-28"),
    lastPaymentDate: new Date("2023-08-01"),
    notifications: "enabled",
    paymentEnabledStatus: 'disabled',
  },
  {
    batchName: "Senior Care Batch 32",
    recipients: 89,
    failed: 3,
    totalAmount: 74048,
    status: "approved",
    firstPaymentDate: new Date("2024-06-26"),
    lastPaymentDate: new Date("2024-06-27"),
    notifications: "enabled",
    paymentEnabledStatus: 'enabled',
  },
  {
    batchName: "Senior Care Batch 33",
    recipients: 69,
    failed: 4,
    totalAmount: 150282,
    status: "approved",
    firstPaymentDate: new Date("2024-08-22"),
    lastPaymentDate: new Date("2024-08-27"),
    notifications: "enabled",
    paymentEnabledStatus: 'enabled',
  },
  {
    batchName: "Food Relief Batch 34",
    recipients: 87,
    failed: 4,
    totalAmount: 235422,
    status: "pending_approval",
    firstPaymentDate: new Date("2023-11-21"),
    lastPaymentDate: new Date("2023-11-26"),
    notifications: "enabled",
    paymentEnabledStatus: 'enabled',
  },
  {
    batchName: "Back-to-School Aid Batch 35",
    recipients: 50,
    failed: 5,
    totalAmount: 10350,
    status: "pending_approval",
    firstPaymentDate: new Date("2023-09-28"),
    lastPaymentDate: new Date("2023-10-01"),
    notifications: "disabled",
    paymentEnabledStatus: 'enabled',
  },
  {
    batchName: "Food Relief Batch 36",
    recipients: 50,
    failed: 4,
    totalAmount: 20650,
    status: "pending_approval",
    firstPaymentDate: new Date("2023-09-22"),
    lastPaymentDate: new Date("2023-09-25"),
    notifications: "enabled",
    paymentEnabledStatus: 'enabled',
  },
  {
    batchName: "Back-to-School Aid Batch 37",
    recipients: 82,
    failed: 4,
    totalAmount: 207378,
    status: "pending_approval",
    firstPaymentDate: new Date("2024-04-30"),
    lastPaymentDate: new Date("2024-05-05"),
    notifications: "enabled",
    paymentEnabledStatus: 'disabled',
  },
  {
    batchName: "Back-to-School Aid Batch 38",
    recipients: 30,
    failed: 5,
    totalAmount: 16410,
    status: "approved",
    firstPaymentDate: new Date("2023-05-29"),
    lastPaymentDate: new Date("2023-06-02"),
    notifications: "disabled",
    paymentEnabledStatus: 'enabled',
  },
  {
    batchName: "Utility Stipend Batch 39",
    recipients: 59,
    failed: 4,
    totalAmount: 164197,
    status: "pending_approval",
    firstPaymentDate: new Date("2023-10-22"),
    lastPaymentDate: new Date("2023-10-26"),
    notifications: "enabled",
    paymentEnabledStatus: 'disabled',
  },
  {
    batchName: "Senior Care Batch 40",
    recipients: 117,
    failed: 2,
    totalAmount: 413829,
    status: "approved",
    firstPaymentDate: new Date("2023-09-18"),
    lastPaymentDate: new Date("2023-09-22"),
    notifications: "disabled",
    paymentEnabledStatus: 'enabled',
  },
  {
    batchName: "Winter Emergency Fund Batch 41",
    recipients: 100,
    failed: 0,
    totalAmount: 429000,
    status: "approved",
    firstPaymentDate: new Date("2023-01-18"),
    lastPaymentDate: new Date("2023-01-23"),
    notifications: "enabled",
    paymentEnabledStatus: 'disabled'
  },
  {
    batchName: "Senior Care Batch 42",
    recipients: 66,
    failed: 2,
    totalAmount: 309540,
    status: "approved",
    firstPaymentDate: new Date("2024-04-06"),
    lastPaymentDate: new Date("2024-04-07"),
    notifications: "disabled",
    paymentEnabledStatus: 'enabled',
  },
  {
    batchName: "Utility Stipend Batch 43",
    recipients: 86,
    failed: 5,
    totalAmount: 91504,
    status: "pending_approval",
    firstPaymentDate: new Date("2023-11-21"),
    lastPaymentDate: new Date("2023-11-26"),
    notifications: "disabled",
    paymentEnabledStatus: 'enabled',
  },
  {
    batchName: "Winter Emergency Fund Batch 44",
    recipients: 36,
    failed: 5,
    totalAmount: 13860,
    status: "pending_approval",
    firstPaymentDate: new Date("2023-09-13"),
    lastPaymentDate: new Date("2023-09-18"),
    notifications: "enabled",
    
    paymentEnabledStatus: 'disabled',
  },
  {
    batchName: "Back-to-School Aid Batch 45",
    recipients: 32,
    failed: 2,
    totalAmount: 91200,
    status: "approved",
    firstPaymentDate: new Date("2024-10-05"),
    lastPaymentDate: new Date("2024-10-10"),
    notifications: "disabled",
    paymentEnabledStatus: 'enabled',
  }
];

export const PaymentsPage = () => {
  const L = useLocalizedStrings()
  const publicConfig = useContext(PublicConfigurationContext);
  const history = useHistory()

  const getMaxBatchTotalAmount = (payments: Payment[]): number => {
    return payments.reduce((max, payment) => {
      return payment.totalAmount > max ? payment.totalAmount : max
    }, 0)
  }
  const maxBatchTotalAmount = getMaxBatchTotalAmount(paymentData)

  const [filters, setFilters] = useState<Filters>({
    paymentEnabledStatus: "",
    approvalStatus: "",
    notifications: "",
    firstPaymentStart: "",
    firstPaymentEnd: "",
    lastPaymentStart: "",
    lastPaymentEnd: "",
    hasFailedPayments: false,
    batchTotalAmountRange: [0, maxBatchTotalAmount]
  })
  const [activeFilters, setActiveFilters] = useState<number>(0)

  const paymentActions: TableAction<Payment>[] = [
    {
      label: "View Recipients",
      onClick: (row) => window.location.href = '/admin-panel/payments/payment-details/payment-id',
      icon: 'UserGroupIcon'
    },
  ]
  
  if (publicConfig && publicConfig.adminPanel === false){
    history.push("/");
  }

  // TODO: make payments context that provides fake payments data to show on page

  // TODO: discuss with Kate and Devin about the design of this card?
  const enabledPaymentsCard = <NumberDisplay
    className="max-w-56"
    title="Enabled Payments"
    value={125} 
    unit="payments"
    iconName="PlayCircleIcon" 
    iconColor="blue"
  />

  const paymentsDataTable = <DataTable 
    columns={paymentColumns} 
    data={paymentData}
    actions={paymentActions}
    enableSearch
    searchLabel="Search payments..."
    filters={(table) => (
      <PaymentsFilter
        filters={filters}
        setFilters={setFilters}
        maxBatchTotalAmount={maxBatchTotalAmount} 
        table={table}
        setActiveFilters={setActiveFilters}
      />
    )}
    activeFilters={activeFilters}
  />

  // TODO: consolidate header footer into a AdminPanelShell Page or something
  
  const header = (
    <div>
      <div>
        <CompactSidebarNavTrigger />
      </div>
      <div>
        <Heading level="h1" className="m-0">Payments</Heading>
        <div className="text-lg text-gray-700">Track your program's performance and impact</div>
      </div>
    </div>
  )
  
  const footer = (
    <div className="w-full flex justify-between">
      <div>
        <Button variant="link" href="/privacy.html" openInNewWindow>
          {L.privacy_policy}
        </Button>
      </div>
      <div>
        <Button variant="link" href="/tos.html" openInNewWindow>
          {L.terms_and_conditions}
        </Button>
      </div>
    </div>
  )
  
  return (
    <Page 
      className="bg-gray-50"
      header={header}
      sidebar={
        <AdminPanelNav />
      }
      footer={footer}
    >
      <div className="flex flex-col gap-4">
        <div>
          {enabledPaymentsCard}
        </div>
        <div>
          <div className="border rounded-md p-4 bg-white">{paymentsDataTable}</div>
        </div>
      </div>
    </Page>
  )
}